import { inject, injectable } from '@wix/typesafe-inversify';
import { getBindingDefinitionsByAppDescriptor } from '@wix/blocks-widget-services/editor';
import type { BlocksAppDescriptor } from '@wix/document-services-types';
import type { ComponentRef, EditorSDK } from '@wix/platform-editor-sdk';
import { EditorType } from '@wix/platform-editor-sdk';
import _ from 'lodash';
import type { IAppDescriptorService } from '../AppDescriptorService/IAppDescriptorService';
import type { IEditorTransactions } from '../EditorTransactions/IEditorTransactions';
import type { IPanelHandlers } from '../PanelHandlers/IPanelHandlers';
import type { ExtendedBlocksAppData } from '../../types/ExtendedBlocksAppData';
import type {
  ExtendedPlatformEvent,
  PlatformActions,
} from '../../types/PlatformTypes';
import {
  addAllComponents,
  addComponent,
} from '../../utils/addElementsUtils/addElementsUtils';
import { getPrimaryConnectionItem } from '../StructureBuilder/structureUtil';
import { removeHash } from '../../utils/removeHash';
import { FEATURE } from '@wix/deeplink-core';
import { AppActions } from '../../utils/appUtils';
import type { IAddWidgetService } from '../AddWidgetService/IAddWidgetService';
import type {
  IEditorReadyOptions,
  IReadyAppData,
} from '../ReadyAppData/IReadyAppData';
import { Services } from '../../ioc/services';
import { monitored } from '../Monitoring/monitored';
import type { IMasterStructureFetcher } from '../MasterStructureFetcher/IMasterStructureFetcher';
import type { IManifestBuilder } from '../ManifestBuilder/IManifestBuilder';
import { ExperimentNames } from '../Experiments/ExperimentNames';
import type { IExperiments } from '../Experiments/IExperiments';
import type { IPromiseVoider } from '../PromiseVoider/IPromiseVoider';

const token = 'token';

@injectable()
export class EditorApp {
  private hasOpenedAddElements: boolean = false;

  constructor(
    @inject(Services.EditorSDK)
    private editorSDK: EditorSDK,
    @inject(Services.AppDescriptorService)
    private appDescriptorService: IAppDescriptorService,
    @inject(Services.ReadyAppData)
    private readyAppDataService: IReadyAppData,
    @inject(Services.MasterStructureFetcher)
    private masterStructureFetcher: IMasterStructureFetcher,
    @inject(Services.AddWidgetService)
    private addWidgetService: IAddWidgetService,
    @inject(Services.PanelHandlers)
    private panelHandlers: IPanelHandlers,
    @inject(Services.EditorTransactions)
    private editorTransactions: IEditorTransactions,
    @inject(Services.ManifestBuilder)
    private manifestBuilder: IManifestBuilder,
    @inject(Services.Experiments)
    private readonly experiments: IExperiments,
    @inject(Services.PromiseVoider)
    private PromiseVoider: IPromiseVoider,
  ) {}

  @monitored('get-binding-definitions')
  public async getBindingDefinitions(componentRef: any) {
    try {
      const definitions = await getBindingDefinitionsByAppDescriptor({
        componentRef,
        editorSDK: this.editorSDK,
        appDescriptor: await this.appDescriptorService.get(),
        panelProps: {
          title: 'Connect Widget',
          height: 467,
          helpId: 'f3e95690-e2c0-4172-bd5f-f6b77335279d',
        },
      });

      return definitions;
    } catch (e) {
      throw new Error('Error getting widget binding definitions');
    }
  }

  @monitored('editor-ready')
  public async editorReady(
    appDefinitionId: string,
    editorReadyOptions: IEditorReadyOptions,
  ) {
    if (editorReadyOptions.firstInstall) {
      await this.addWidgetService.addRequiredWidgetsToStage();
    }

    await this.panelHandlers.initialize();
    await this.PromiseVoider.executeAndVoid(() => this.checkMonitoring());
  }

  public async listConnectedWidgets() {
    const allControllers = await this.editorSDK.controllers.listAllControllers(
      token,
    );
    const appDescriptor = await this.appDescriptorService.get();
    return _.filter(
      await Promise.all(
        _.map(allControllers, async ({ controllerRef }: any) =>
          _.assign({}, controllerRef, {
            widgetType: (
              await this.editorSDK.controllers.getData(token, {
                controllerRef,
              })
            ).config.type,
          }),
        ),
      ),
      (controller: any) =>
        this.isWidgetDefined(controller.widgetType, appDescriptor),
    );
  }

  async handleAddElementsCompClicked({ widgetRef, label }: any) {
    await this.editorTransactions.runAndWaitForApproval(async () => {
      await addComponent(
        this.editorSDK,
        this.masterStructureFetcher,
        widgetRef,
        label,
      );
    });
  }

  @monitored('on-event-widget-gfpp-clicked')
  async handleWidgetGfppClicked({
    componentRef,
    id,
  }: {
    componentRef: ComponentRef;
    id: string;
  }) {
    switch (id) {
      case 'addElements':
        await this.panelHandlers.openAddElementsPanel(componentRef);
        return;
      default:
        break;
    }
    await this.panelHandlers.tryOpenUserPanel(componentRef, id);
  }

  async handleOpenDashboardClicked() {
    await this.editorSDK.editor.openDashboardPanel('', {
      url: `app/${this.readyAppDataService.appDefinitionId}`,
      closeOtherPanels: false,
    });
  }

  async handleManagePagesClicked() {
    await this.editorSDK.editor.deeplink.show('', {
      type: FEATURE.pagesPanel,
      params: [this.readyAppDataService.appDefinitionId],
    });
  }

  async handleDiscoverWidgetsClicked() {
    await this.editorSDK.editor.deeplink.show('', {
      type: FEATURE.addPanel,
      params: [this.readyAppDataService.appDefinitionId],
    });
  }
  @monitored('on-event-component-gfpp-clicked')
  async handleComponentGfppClicked({
    componentRef,
    id,
  }: {
    componentRef: ComponentRef;
    id: string;
  }) {
    await this.panelHandlers.tryOpenUserPanel(componentRef, id);
  }

  @monitored('on-event')
  async onEvent({
    eventType,
    eventPayload,
  }: {
    eventType: ExtendedPlatformEvent['eventType'];
    eventPayload?: any;
  }) {
    switch (eventType) {
      case 'refreshControllers': {
        await this.handleRefreshControllers();
        break;
      }
      case 'componentDeleted':
        await this.handleComponentDeleted(eventPayload);
        break;
      case 'componentGfppClicked':
        await this.handleComponentGfppClicked(eventPayload);
        break;
      case 'widgetGfppClicked':
        await this.handleWidgetGfppClicked(eventPayload);
        break;
      case 'addElementsAllCompsClicked':
      case 'addElementsResetClicked':
        await this.handleAddElementsAllCompsClicked(eventPayload);
        break;
      case 'addElementsCompClicked':
        await this.handleAddElementsCompClicked(eventPayload);
        break;
      case 'addWidget':
        await this.addWidgetService.addWidget({
          widgetPageId: eventPayload.widgetType,
          position: eventPayload.position,
          size: eventPayload.size,
          variationPageId: eventPayload.variationPageId,
          presetId: eventPayload.presetId,
          containerRef: eventPayload.containerRef,
          shouldGetMobilePresetFromEditorPresence:
            eventPayload.shouldGetMobilePresetFromEditorPresence ?? true,
        });
        break;
      case 'resetWidget':
        await this.resetWidget(eventPayload.controllerRef);
        break;
      case 'stateDuplicated':
        await this.handleStateBoxStateDuplicated(eventPayload);
        break;
      case 'appActionClicked':
        await this.handleActionClick(eventPayload);
        break;
      default:
        break;
    }
  }

  @monitored('on-event-handle-action-click')
  public async handleActionClick(eventPayload: any) {
    switch (eventPayload?.actionId) {
      case AppActions.openDashboard:
        return this.handleOpenDashboardClicked();
      case AppActions.managePages:
        return this.handleManagePagesClicked();
      case AppActions.discoverMoreWidgets:
        return this.handleDiscoverWidgetsClicked();
      default:
        break;
    }
  }
  @monitored('handle-action-app-updated')
  public async handleAppUpdate(updateType: 'MINOR' | 'MAJOR') {
    await this.panelHandlers.initialize();
    await this.updateApp(updateType);
  }

  @monitored('handle-action')
  public async handleAction({ type, payload }: PlatformActions) {
    switch (type) {
      case 'appUpdated': {
        await this.handleAppUpdate(_.get(payload, 'updateType'));
        break;
      }
      case 'removeApp': {
        await this.handleRemove();
        break;
      }
      default:
        break;
    }
  }

  getDefaultVariationId(widgets: any, widgetPageId: any) {
    const [firstVariation] = widgets[widgetPageId].variations || [];
    if (firstVariation) {
      return removeHash(firstVariation.rootCompId);
    }
  }

  async resetWidget(controllerRef: any) {
    await this.editorTransactions.runAndWaitForApproval(async () => {
      const { config } = await this.editorSDK.controllers.getData(token, {
        controllerRef,
      });
      const { variationPageId: currentVariationId } = config;
      const variationId =
        currentVariationId ||
        this.getDefaultVariationId(
          (await this.appDescriptorService.get()).widgets,
          config.type,
        );
      // Use changeVariation with current variationPageId to reset to latest widget structure
      await this.editorSDK.application.appStudioWidgets.changeVariation(token, {
        componentRef: controllerRef,
        variationId,
        keepOverrides: false,
      });
    });
  }

  @monitored('get-app-manifest')
  async getAppManifest() {
    const manifest = await this.manifestBuilder.generateAppManifest(
      await this.appDescriptorService.get(),
      await this.getBlocksAppData(),
      this.readyAppDataService.editorType === EditorType.Responsive,
    );

    return manifest;
  }

  private async handleStateBoxStateDuplicated({ newDuplicatedStateRef }: any) {
    const connections =
      await this.editorSDK.document.controllers.listConnections(token, {
        componentRef: newDuplicatedStateRef,
      });
    const { controllerRef: appWidgetRef } =
      getPrimaryConnectionItem(connections);
    await this.editorSDK.document.controllers.disconnect(token, {
      connectToRef: newDuplicatedStateRef,
      controllerRef: appWidgetRef,
    });
  }
  @monitored('on-event-component-deleted')
  private async handleComponentDeleted({ connections }: any) {
    if (!connections.length) {
      return;
    }
    const [{ controllerRef: widgetRef }] = connections;
    if (!this.hasOpenedAddElements) {
      // there is old sites with apps without widget, so hiding/deleting their component cause error
      // so need check if widget was added on consumption
      const widgetExists = await this.editorSDK.components.data.get('token', {
        componentRef: widgetRef,
      });
      if (!widgetExists) {
        return;
      }
      await this.panelHandlers.openAddElementsPanel(widgetRef);
      this.hasOpenedAddElements = true;
    }
  }

  @monitored('on-event-add-elements')
  private async handleAddElementsAllCompsClicked({ widgetRef }: any) {
    await this.editorTransactions.runAndWaitForApproval(async () => {
      await addAllComponents(
        this.editorSDK,
        this.masterStructureFetcher,
        widgetRef,
      );
    });
  }

  private async removeComponents(componentRefs: ComponentRef[]) {
    const removeOperations = _.map(componentRefs, (componentRef) =>
      this.editorSDK.components.remove(token, {
        componentRef,
      }),
    );
    await Promise.all(removeOperations);
  }

  @monitored('handle-action-remove-app')
  private async handleRemove() {
    await this.editorTransactions.runAndWaitForApproval(async () => {
      const refComps =
        await this.editorSDK.components.refComponents.getAllAppRefComponents(
          token,
        );
      await this.removeComponents(refComps);
      const controllerRefs = await this.listConnectedWidgets();
      await this.removeComponents(controllerRefs);
    });
  }

  @monitored('on-event-refresh-controllers')
  private async handleRefreshControllers() {
    await this.editorSDK.document.application.livePreview.refresh(token, {
      shouldFetchData: false,
      source: 'refreshControllers',
    });
  }

  private isWidgetDefined(widgetId: any, appDescriptor: BlocksAppDescriptor) {
    return !_.isUndefined(appDescriptor.widgets[widgetId]);
  }

  private getBlocksAppData() {
    return this.editorSDK.tpa.app.getDataByAppDefId(
      token,
      this.readyAppDataService.appDefinitionId,
    ) as Promise<ExtendedBlocksAppData>;
  }

  private async updateApp(updateType: 'MINOR' | 'MAJOR') {
    const resetWidgetFacade = async (controllerId: string) => {
      const controllerRef = await this.editorSDK.components.getById('token', {
        id: controllerId,
      });
      await this.resetWidget(controllerRef);
    };

    const resetAllWidgets = async () => {
      const controllerRefs = await this.listConnectedWidgets();
      if (controllerRefs.length > 0) {
        for (const controllerRef of controllerRefs) {
          await resetWidgetFacade(controllerRef.id);
        }
      }
    };

    const updateAppResources = async () => {
      await this.editorSDK.application.reloadManifest();
    };

    await updateAppResources();

    if (updateType === 'MAJOR') {
      await resetAllWidgets();
    }
  }

  @monitored('check-monitoring')
  private async checkMonitoring() {
    if (this.experiments.enabled(ExperimentNames.FailTransactionWithError)) {
      throw new Error('Test Error to check transactional monitoring');
    }
  }
}
